import axios from 'axios';

// Interfaces para respuestas de API
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T | null;
}

// Obtener la URL base de la API desde variables de entorno (compatible con Vite)
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Crear instancia base de axios con la URL de la API
export const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Configurar interceptores para agregar token de autenticación
API.interceptors.request.use(
  (config) => {
    // Obtener el token en tiempo real de localStorage en cada petición
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Token disponible en la petición:', token.substring(0, 10) + '...');
    } else {
      console.warn('Token no disponible para la petición');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores de autenticación (401)
API.interceptors.response.use(
  (response) => response,
  (error) => {
    // Si el error es 401 Unauthorized, limpiar token
    if (error.response && error.response.status === 401) {
      console.warn('Error 401: Sesión expirada o inválida');
      localStorage.removeItem('token');
      // Redireccionar a login si es necesario
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Función auxiliar para manejar respuestas de la API
export function handleApiResponse<T>(response: any): ApiResponse<T> {
  return response.data;
}

/**
 * Convierte un mensaje de error (proveniente del backend o de red) en un texto
 * amigable para el usuario.
 * - Timeouts de cURL o de red          → "El proveedor no respondió…"
 * - Errores de conexión del frontend   → "Sin conexión con el servidor…"
 * - Cualquier otro mensaje del API     → lo devuelve tal cual
 */
export function friendlyError(message: string): string {
  const m = (message || '').toLowerCase();
  if (
    m.includes('curl error 28') ||
    m.includes('operation timed out') ||
    m.includes('timed out') ||
    m.includes('timeout') ||
    m.includes('0 bytes received')
  ) {
    return 'El proveedor no respondió a tiempo. Verifique el número de servicio e intente nuevamente.';
  }
  if (
    m.includes('network error') ||
    m.includes('failed to fetch') ||
    m.includes('sin conexión') ||
    m.includes('econnrefused')
  ) {
    return 'Sin conexión con el servidor. Verifique su red e intente nuevamente.';
  }
  return message;
}

// Función auxiliar para manejar errores de la API
export function handleApiError(error: any): ApiResponse {
  console.error('API Error:', error);
  
  // Si el error tiene una respuesta del servidor
  if (error.response && error.response.data) {
    return {
      success: false,
      message: error.response.data.message || 'Error en la operación',
      data: null
    };
  }
  
  // Error de red o de otro tipo
  return {
    success: false,
    message: error.message || 'Error de conexión',
    data: null
  };
}
